/**
 * O fechamento regulatório do período (`OB-06`, ADR-067).
 *
 * Todo mês a concessionária entrega ao poder concedente o que passou: o resumo, a planilha das
 * evasões com as fraudes marcadas, a ficha e a foto de cada fraude e, por trimestre, a memória
 * de cálculo da compensação. Esta tela mostra o que o pacote vai levar — para conferir antes
 * de baixar — e baixa o ZIP.
 *
 * **Os números são os da régua de Indicadores do mesmo período e concessão**: o servidor lê o
 * mesmo resumo, e há teste. Cada linha do resumo abre a fila daquele recorte, com a mesma
 * contagem (`AC14`), como a régua. A tela não calcula nada.
 *
 * **A foto que sai é a anonimizada** (a cabine borrada, a placa legível): a de pórtico passa
 * pela IA de anonimização quando a supervisão confirma a fraude, e a tela diz, em cada ficha,
 * se a foto está pronta, na fila ou por que não há.
 *
 * É da supervisão (supervisor, apresentador e administrador): o pacote leva placas e fotos de
 * um período inteiro, e cada download vai à trilha administrativa.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";

import { ErroDaApi, api } from "../api/cliente";
import { useSessao } from "../api/contexto-sessao";
import { comoParametros } from "../api/tipos";
import type {
  CompensacaoDaConcessao,
  FraudeNoFechamento,
  LinhaDoResumo,
  OpcoesDoFechamento,
  ResumoDoFechamento,
} from "../api/tipos";
import { Badge, Botao, Estado, Painel } from "../componentes/base";
import { Icone } from "../componentes/icones";
import { supervisiona } from "../componentes/navegacao";
import { dataEHora, numeroLongo, reais } from "../formato";
import { SITUACAO_DA_FOTO, enderecoDoPacote } from "./fechamento";

/** As linhas do resumo que abrem uma subdivisão da anterior: recuadas na tabela. */
const DENTRO_DE: ReadonlySet<string> = new Set([
  "pago_por_tag",
  "pago_no_portal",
  "pago_pela_osa",
  "baixa_manual",
  "aguardando_pagamento",
  "isentos",
  "validas_nao_pagas",
  "validas_nunca_cobradas",
  "nao_pagas_com_prova",
  "nao_pagas_sem_prova",
]);

function mensagem(falha: unknown, padrao: string): string {
  return falha instanceof ErroDaApi ? falha.message : padrao;
}

export function FechamentoRegulatorio() {
  const { quem } = useSessao();
  const podeFechar = supervisiona(quem?.perfil);
  const [opcoes, definirOpcoes] = useState<OpcoesDoFechamento | null>(null);
  const [concessao, definirConcessao] = useState("");
  const [periodo, definirPeriodo] = useState("");
  const [resumo, definirResumo] = useState<ResumoDoFechamento | null>(null);
  const [erro, definirErro] = useState<string | null>(null);
  // "Tentar de novo" relê tudo: as opções (se foram elas que falharam) e o resumo.
  const [tentativa, definirTentativa] = useState(0);
  const navegar = useNavigate();

  useEffect(() => {
    if (!podeFechar) return;
    let valida = true;
    void (async () => {
      try {
        const lidas = await api<OpcoesDoFechamento>(
          "/tollvision/fechamentos/opcoes",
        );
        if (!valida) return;
        definirOpcoes(lidas);
        definirConcessao((atual) => atual || (lidas.concessoes[0]?.id ?? ""));
        definirPeriodo((atual) => atual || lidas.padrao);
      } catch (falha) {
        if (valida)
          definirErro(mensagem(falha, "Não foi possível ler os períodos."));
      }
    })();
    return () => {
      valida = false;
    };
  }, [podeFechar, tentativa]);

  // Trocar de período antes de a resposta anterior chegar não pode deixar na tela o período
  // de antes: a resposta que chega depois da troca é descartada.
  useEffect(() => {
    if (!concessao || !periodo) return;
    let valida = true;
    definirResumo(null);
    void (async () => {
      try {
        const lido = await api<ResumoDoFechamento>(
          `/tollvision/fechamentos?${new URLSearchParams({
            concessao_id: concessao,
            periodo,
          })}`,
        );
        if (!valida) return;
        definirResumo(lido);
        definirErro(null);
      } catch (falha) {
        if (valida)
          definirErro(mensagem(falha, "Não foi possível montar o fechamento."));
      }
    })();
    return () => {
      valida = false;
    };
  }, [concessao, periodo, tentativa]);

  const tentarDeNovo = useCallback(() => {
    definirErro(null);
    definirTentativa((n) => n + 1);
  }, []);

  const meses = useMemo(
    () => opcoes?.periodos.filter((p) => !p.trimestral) ?? [],
    [opcoes],
  );
  const trimestres = useMemo(
    () => opcoes?.periodos.filter((p) => p.trimestral) ?? [],
    [opcoes],
  );

  if (!podeFechar) {
    return (
      <Estado
        tipo="bloqueado"
        titulo="O fechamento regulatório é da supervisão."
        detalhe="Quem fecha o período e baixa o pacote para o poder concedente é o supervisor ou o administrador."
      />
    );
  }

  /** A linha do resumo abre a fila daquele recorte no período e na concessão (`AC14`). */
  function abrirRecorte(chave: string) {
    if (!resumo) return;
    navegar(
      `/senatran?${comoParametros(
        {
          desde: resumo.periodo.inicio,
          ate: resumo.periodo.fim,
          concessao_id: resumo.concessao.id,
        },
        { recorte: chave },
      )}`,
    );
  }

  const acimaDoLimite =
    resumo != null && resumo.evasoes > resumo.limite_das_evasoes;
  const pendentes =
    resumo?.fraudes.filter((f) => f.foto === "PENDENTE").length ?? 0;

  return (
    <>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h1 className="text-titulo-tela leading-none font-extrabold tracking-[-0.01em] text-tinta uppercase">
            Fechamento regulatório
          </h1>
          <p className="text-subtitulo-tela text-tinta-3">
            O que a concessionária entrega ao poder concedente no fim do mês e
            do trimestre
          </p>
        </div>
        <div className="ml-auto flex min-w-0 max-w-full flex-wrap items-center gap-2">
          <select
            aria-label="Concessão"
            value={concessao}
            onChange={(e) => definirConcessao(e.target.value)}
            className="min-h-8 max-w-full min-w-0 rounded-botao border border-linha bg-painel-2 px-2 text-[11.5px] text-tinta"
          >
            {opcoes?.concessoes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome}
              </option>
            ))}
          </select>
          <select
            aria-label="Período"
            value={periodo}
            onChange={(e) => definirPeriodo(e.target.value)}
            className="min-h-8 rounded-botao border border-linha bg-painel-2 px-2 text-[11.5px] text-tinta"
          >
            <optgroup label="Mês">
              {meses.map((p) => (
                <option key={p.codigo} value={p.codigo}>
                  {p.rotulo}
                  {p.fechado ? "" : " (em curso)"}
                </option>
              ))}
            </optgroup>
            <optgroup label="Trimestre">
              {trimestres.map((p) => (
                <option key={p.codigo} value={p.codigo}>
                  {p.rotulo}
                  {p.fechado ? "" : " (em curso)"}
                </option>
              ))}
            </optgroup>
          </select>
          <Link
            to="/indicadores"
            className="inline-flex min-h-8 items-center gap-1.5 rounded-botao border border-linha bg-painel-2 px-2.5 text-[11.5px] font-semibold text-tinta-2 hover:bg-realce"
          >
            ‹ Voltar aos Indicadores
          </Link>
          {resumo && !acimaDoLimite ? (
            <a
              href={enderecoDoPacote(
                resumo.concessao.id,
                resumo.periodo.codigo,
              )}
              title="ZIP com o resumo, a compensação, as fraudes com as fotos anonimizadas, as evasões e o manifesto com o SHA-256 de cada arquivo."
              className="inline-flex min-h-8 items-center gap-1.5 rounded-botao border border-azul-cheio bg-azul-cheio px-2.5 text-[11.5px] font-semibold text-white hover:bg-azul"
            >
              <Icone.Baixar />
              {resumo.periodo.fechado
                ? "Baixar o pacote (.zip)"
                : "Baixar a prévia (.zip)"}
            </a>
          ) : (
            <span
              aria-disabled="true"
              title={
                acimaDoLimite
                  ? "O período passa do teto de evasões do pacote: feche mês a mês."
                  : "Escolha a concessão e o período."
              }
              className="inline-flex min-h-8 cursor-not-allowed items-center gap-1.5 rounded-botao border border-linha bg-painel-2 px-2.5 text-[11.5px] font-semibold text-tinta-3"
            >
              <Icone.Baixar />
              Baixar o pacote (.zip)
            </span>
          )}
        </div>
      </div>

      {erro ? (
        <Painel titulo="Fechamento">
          <Estado
            tipo="erro"
            titulo={erro}
            acao={<Botao onClick={tentarDeNovo}>Tentar de novo</Botao>}
          />
        </Painel>
      ) : opcoes && opcoes.concessoes.length === 0 ? (
        <Painel titulo="Fechamento">
          <Estado
            tipo="vazio"
            titulo="Nenhuma concessão no seu recorte."
            detalhe="O fechamento é por concessão: peça ao administrador o acesso à concessão que você fecha."
          />
        </Painel>
      ) : !resumo ? (
        <Painel titulo="Fechamento">
          <Estado tipo="carregando" titulo="Montando o fechamento…" />
        </Painel>
      ) : (
        <div className="grid min-h-0 auto-rows-max content-start gap-3 overflow-y-auto pb-1">
          <Avisos
            resumo={resumo}
            pendentes={pendentes}
            acimaDoLimite={acimaDoLimite}
          />
          <div className="grid auto-rows-max gap-3 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
            <Painel
              titulo={`Resumo — ${resumo.periodo.rotulo}`}
              rodape={
                <span>
                  Os números da régua de Indicadores do mesmo período. Clique
                  numa linha para abrir a fila dela.
                </span>
              }
            >
              <TabelaDoResumo linhas={resumo.linhas} aoAbrir={abrirRecorte} />
            </Painel>
            <Painel
              titulo="Compensação — memória de cálculo"
              acoes={
                resumo.simulada ? (
                  <Badge cor="amarelo">simulação</Badge>
                ) : undefined
              }
              rodape={
                <span>
                  O que o poder concedente devolveria se nada mais fosse pago,
                  pela regra do regime da concessão.
                </span>
              }
            >
              <MemoriaDaCompensacao resumo={resumo} />
            </Painel>
          </div>
          <Painel
            titulo={`Fraudes confirmadas (${resumo.fraudes.length})`}
            rolagemPropria
            rodape={
              <span>
                A foto que vai no pacote é a anonimizada: a cabine borrada, a
                placa legível. O original não sai do cockpit.
              </span>
            }
          >
            {resumo.fraudes.length === 0 ? (
              <Estado
                tipo="vazio"
                titulo="Nenhuma fraude confirmada no período."
                detalhe="A suspeita do operador só entra aqui depois que a supervisão confirma."
              />
            ) : (
              <div className="min-h-0 overflow-auto">
                <TabelaDasFraudes fraudes={resumo.fraudes} />
              </div>
            )}
          </Painel>
        </div>
      )}
    </>
  );
}

function Avisos({
  resumo,
  pendentes,
  acimaDoLimite,
}: {
  resumo: ResumoDoFechamento;
  pendentes: number;
  acimaDoLimite: boolean;
}) {
  const avisos: string[] = [];
  if (!resumo.periodo.fechado) {
    avisos.push(
      `${resumo.periodo.rotulo} ainda está em curso: o pacote é uma prévia, e os números andam até o fim do período.`,
    );
  }
  if (pendentes > 0) {
    avisos.push(
      pendentes === 1
        ? "Uma foto de fraude ainda está na fila da anonimização (leva minutos). Baixar agora leva o pacote sem ela."
        : `${numeroLongo(pendentes)} fotos de fraude ainda estão na fila da anonimização (leva minutos). Baixar agora leva o pacote sem elas.`,
    );
  }
  if (acimaDoLimite) {
    avisos.push(
      `O período tem ${numeroLongo(resumo.evasoes)} evasões, e o pacote vai até ${numeroLongo(resumo.limite_das_evasoes)}: feche mês a mês.`,
    );
  }
  if (avisos.length === 0) return null;
  return (
    <ul
      aria-label="Avisos do fechamento"
      className="grid gap-1 rounded-cartao border border-amarelo-borda bg-amarelo-fundo px-3 py-2 text-[11.5px] text-tinta"
    >
      {avisos.map((a) => (
        <li key={a} className="flex items-start gap-1.5">
          <span className="text-amarelo">
            <Icone.Alerta />
          </span>
          {a}
        </li>
      ))}
    </ul>
  );
}

function TabelaDoResumo({
  linhas,
  aoAbrir,
}: {
  linhas: LinhaDoResumo[];
  aoAbrir: (chave: string) => void;
}) {
  return (
    <table className="w-full border-collapse text-[11.5px]">
      <thead className="bg-painel-2 text-left text-rotulo tracking-[0.06em] text-tinta-3 uppercase">
        <tr>
          <th className="px-2 py-1.5">Linha</th>
          <th className="px-2 py-1.5 text-right">Passagens</th>
          <th className="px-2 py-1.5 text-right">Valor</th>
        </tr>
      </thead>
      <tbody>
        {linhas.map((l) => (
          <tr
            key={l.chave}
            className="border-b border-linha-suave hover:bg-realce"
          >
            <td
              className={`px-2 py-1.5 ${DENTRO_DE.has(l.chave) ? "pl-5 text-tinta-2" : "font-semibold text-tinta"}`}
            >
              <button
                type="button"
                title="Abrir a fila deste recorte, no período e na concessão"
                className="text-left hover:underline"
                onClick={() => aoAbrir(l.chave)}
              >
                {l.rotulo}
              </button>
            </td>
            <td className="mono px-2 py-1.5 text-right text-tinta">
              {numeroLongo(l.quantidade)}
            </td>
            <td className="mono px-2 py-1.5 text-right text-tinta-2">
              {l.valor_centavos == null
                ? "sem tarifa"
                : reais(l.valor_centavos)}
              {l.sem_tarifa > 0 && l.valor_centavos != null && (
                <span
                  className="block text-[10px] text-tinta-3"
                  title="Passagens sem tarifa não entram na soma."
                >
                  {numeroLongo(l.sem_tarifa)} sem tarifa
                </span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function percentual(valor: number): string {
  return `${valor.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`;
}

function MemoriaDaCompensacao({ resumo }: { resumo: ResumoDoFechamento }) {
  return (
    <div className="grid gap-3">
      <div className="flex items-baseline gap-2">
        <b className="mono text-[26px] leading-none text-tinta">
          {reais(resumo.compensacao_centavos)}
        </b>
        <span className="text-[11px] text-tinta-3">compensação estimada</span>
      </div>
      {resumo.compensacao.map((c) => (
        <PorRegime key={`${c.concessao_id}-${c.regime}`} compensacao={c} />
      ))}
      {resumo.nota && (
        <p className="text-[10.5px] leading-snug text-tinta-3">{resumo.nota}</p>
      )}
    </div>
  );
}

function PorRegime({
  compensacao: c,
}: {
  compensacao: CompensacaoDaConcessao;
}) {
  // A regra federal (`OB-08`) só devolve a válida com a prova da notificação: a base das
  // válidas é a das que têm a prova, e a sem a prova aparece à parte, a zero.
  const validas = c.exige_protocolo
    ? [
        {
          rotulo: "Válidas não pagas com a prova da notificação",
          base: c.base_validas,
          percentual: c.percentual_validas,
          parte: c.das_validas,
        },
        {
          rotulo: "Válidas não pagas sem a prova da notificação",
          base: c.base_validas_sem_prova,
          percentual: 0,
          parte: 0,
        },
      ]
    : [
        {
          rotulo: "Válidas não pagas",
          base: c.base_validas,
          percentual: c.percentual_validas,
          parte: c.das_validas,
        },
      ];
  const classes = [
    ...validas,
    {
      rotulo: "Inválidas fraudulentas",
      base: c.base_fraudulentas,
      percentual: c.percentual_fraudulentas,
      parte: c.das_fraudulentas,
    },
    {
      rotulo: "Inválidas",
      base: c.base_invalidas,
      percentual: c.percentual_invalidas,
      parte: c.das_invalidas,
    },
  ];
  return (
    <section
      aria-label={`Compensação: ${c.regime}`}
      className="grid gap-1 rounded-cartao border border-linha bg-painel-2 p-2.5"
    >
      <span className="flex flex-wrap items-center gap-1.5 text-[11px] text-tinta-3">
        <b className="text-tinta-2">{c.regime}</b>
        {c.simulada && <Badge cor="amarelo">simulação</Badge>}
      </span>
      <table className="w-full border-collapse text-[11.5px]">
        <thead className="text-left text-rotulo tracking-[0.06em] text-tinta-3 uppercase">
          <tr>
            <th className="py-1">Classe</th>
            <th className="py-1 text-right">Base</th>
            <th className="py-1 text-right">%</th>
            <th className="py-1 text-right">Parte</th>
          </tr>
        </thead>
        <tbody>
          {classes.map((linha) => (
            <tr key={linha.rotulo} className="border-t border-linha-suave">
              <td className="py-1 text-tinta-2">{linha.rotulo}</td>
              <td className="mono py-1 text-right text-tinta-2">
                {reais(linha.base)}
              </td>
              <td className="mono py-1 text-right text-tinta-2">
                {percentual(linha.percentual)}
              </td>
              <td className="mono py-1 text-right text-tinta">
                {reais(linha.parte)}
              </td>
            </tr>
          ))}
          <tr className="border-t border-linha">
            <td className="py-1 font-semibold text-tinta" colSpan={3}>
              Total
            </td>
            <td className="mono py-1 text-right font-semibold text-tinta">
              {reais(c.total)}
            </td>
          </tr>
        </tbody>
      </table>
      <p className="text-[10px] leading-snug text-tinta-3">Regra: {c.fonte}.</p>
    </section>
  );
}

function TabelaDasFraudes({ fraudes }: { fraudes: FraudeNoFechamento[] }) {
  return (
    <table className="w-full min-w-[900px] border-collapse text-[11.5px]">
      <thead className="sticky top-0 bg-painel-2 text-left text-rotulo tracking-[0.06em] text-tinta-3 uppercase">
        <tr>
          <th className="px-2 py-1.5">Passagem</th>
          <th className="px-2 py-1.5">Praça</th>
          <th className="px-2 py-1.5">Placa lida</th>
          <th className="px-2 py-1.5 text-right">Valor</th>
          <th className="px-2 py-1.5">Motivo</th>
          <th className="px-2 py-1.5">Suspeita</th>
          <th className="px-2 py-1.5">Confirmação</th>
          <th className="px-2 py-1.5">Vista</th>
          <th className="px-2 py-1.5">Foto</th>
        </tr>
      </thead>
      <tbody>
        {fraudes.map((f) => {
          const foto = SITUACAO_DA_FOTO[f.foto];
          return (
            <tr
              key={f.transito_id}
              className="border-b border-linha-suave align-top"
            >
              <td className="px-2 py-1.5 whitespace-nowrap text-tinta-2">
                {dataEHora(f.passagem)}
              </td>
              <td className="px-2 py-1.5 text-tinta-2">{f.praca}</td>
              <td className="mono px-2 py-1.5 font-bold text-tinta">
                {f.placa ?? "—"}
              </td>
              <td className="mono px-2 py-1.5 text-right text-tinta-2">
                {f.valor_centavos == null
                  ? "sem tarifa"
                  : reais(f.valor_centavos)}
              </td>
              <td className="px-2 py-1.5 text-tinta-2">
                {f.motivo_rotulo ?? "—"}
              </td>
              <td className="px-2 py-1.5 text-tinta-2">
                {f.suspeita_por ?? "—"}
                {f.suspeita_em && (
                  <span className="block text-[10px] text-tinta-3">
                    {dataEHora(f.suspeita_em)}
                  </span>
                )}
              </td>
              <td className="px-2 py-1.5 text-tinta-2">
                {f.confirmada_por ?? "—"}
                {f.confirmada_em && (
                  <span className="block text-[10px] text-tinta-3">
                    {dataEHora(f.confirmada_em)}
                  </span>
                )}
              </td>
              <td className="px-2 py-1.5 text-tinta-2">{f.vista ?? "—"}</td>
              <td className="px-2 py-1.5">
                <Badge cor={foto.cor}>{foto.texto}</Badge>
                {f.observacao && f.foto !== "PRONTA" && (
                  <span className="mt-0.5 block max-w-[240px] text-[10px] leading-snug text-tinta-3">
                    {f.observacao}
                  </span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
